// ============================
// App.jsx — + Week vs Week analysis, + Averages column (7d/30d/All)
// - Keeps: questions ÷10 on graph, calendar date-only, linear red→green cell color
// - UI: Tailwind + framer-motion + recharts, no alert/confirm, toast system
// ============================

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import "tailwindcss/tailwind.css";

// ============================
// Helpers
// ============================

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const fmtKey = (d) => d.toISOString().slice(0, 10);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

const defaultDay = () => ({
  targets: [],
  stats: { hours: 0, tests: 0, questions: 0, mistakes: [] },
});

const makeId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const calcProductivity = (day) => {
  if (!day?.targets?.length) return 0;
  const done = day.targets.filter((t) => t.done).length;
  return Math.round((done / day.targets.length) * 100);
};

const diffDays = (from, to) => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  const MS = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((b - a) / MS));
};

const rangeDays = (from, to) => {
  const out = [];
  const cur = new Date(from);
  while (cur <= to) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const toCSV = (rows) => {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return rows.map((r) => r.map(esc).join(",")).join("\n");
};

// ---------- New date helpers for week & rolling windows ----------
/** Monday-first start of week */
const startOfWeek = (d) => {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0..6 for Mon..Sun
  const start = new Date(date);
  start.setDate(date.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
};
const endOfWeek = (d) => {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const lastNDays = (n, endDate = new Date()) => {
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const start = new Date(end);
  start.setDate(end.getDate() - (n - 1));
  return rangeDays(start, end);
};

const keysBetween = (from, to) => rangeDays(from, to).map(fmtKey);

const sumStatsForKeys = (data, keys) => {
  let hours = 0, tests = 0, questions = 0;
  let prodSum = 0, prodCount = 0;
  let targetsTotal = 0, targetsDone = 0;

  for (const k of keys) {
    const day = data[k];
    if (!day) continue;
    hours += day.stats?.hours || 0;
    tests += day.stats?.tests || 0;
    questions += day.stats?.questions || 0;

    const p = calcProductivity(day);
    if (!Number.isNaN(p)) {
      prodSum += p;
      prodCount += 1;
    }
    const tAll = day.targets?.length || 0;
    targetsTotal += tAll;
    targetsDone += day.targets?.filter((t) => t.done).length || 0;
  }
  const avgProd = prodCount ? Math.round(prodSum / prodCount) : 0;
  return { hours, tests, questions, avgProd, targetsTotal, targetsDone };
};

const averageOf = (total, count) => (count > 0 ? +(total / count).toFixed(2) : 0);

// ============================
// Local Storage Hook
// ============================

function useLocalStore(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {}
  }, [key, state]);
  return [state, setState];
}

// ============================
// Toasts (no alert/confirm)
// ============================

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = "info", ttl = 2200) => {
    const id = makeId();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl);
  }, []);
  return { toasts, addToast };
}

function ToastHost({ toasts }) {
  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 w-[min(90vw,320px)]">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            className={[
              "rounded-xl px-4 py-3 shadow-lg border",
              t.type === "success" && "bg-emerald-600/20 border-emerald-500/40 text-emerald-200",
              t.type === "error" && "bg-rose-600/20 border-rose-500/40 text-rose-200",
              t.type === "info" && "bg-slate-700/60 border-slate-500/40 text-slate-100",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {t.msg}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ============================
// Countdown Header
// ============================

function CountdownHeader({ targetDateStr, setTargetDateStr, mission, setMission }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const targetDate = useMemo(() => new Date(targetDateStr), [targetDateStr]);
  const daysLeft = diffDays(new Date(), targetDate);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-2xl bg-slate-900/60 border border-slate-700/40 shadow-lg"
    >
      <div className="flex justify-between items-center gap-3">
        <h1 className="text-3xl font-orbitron bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 text-transparent bg-clip-text">
          JEE 2026 Countdown
        </h1>
        <button
          onClick={() => setSettingsOpen((v) => !v)}
          className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700"
          aria-label="Toggle settings"
        >
          ⚙
        </button>
      </div>
      <div className="mt-4 text-slate-200 text-sm md:text-base">
        <span className="opacity-70">MISSION:</span>{" "}
        <span className="font-semibold">{mission}</span>
      </div>
      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div className="text-6xl font-black text-cyan-300 leading-none">{daysLeft}</div>
        <div className="text-sm text-slate-400">Target: {targetDate.toLocaleDateString()}</div>
      </div>
      {settingsOpen && (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-xs mb-1 block text-slate-400">Mission</span>
            <input
              value={mission}
              onChange={(e) => setMission(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-cyan-500"
            />
          </label>
          <label className="block">
            <span className="text-xs mb-1 block text-slate-400">Target Date</span>
            <input
              type="date"
              value={targetDateStr}
              onChange={(e) => setTargetDateStr(e.target.value)}
              className="w-full px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-cyan-500"
            />
          </label>
        </div>
      )}
    </motion.div>
  );
}

// ============================
// Month Switcher
// ============================

function MonthSwitcher({ selected, setSelected }) {
  const monthName = selected.toLocaleString(undefined, { month: "long", year: "numeric" });
  const prevMonth = () => {
    const d = new Date(selected);
    d.setMonth(d.getMonth() - 1);
    setSelected(d);
  };
  const nextMonth = () => {
    const d = new Date(selected);
    d.setMonth(d.getMonth() + 1);
    setSelected(d);
  };
  const today = () => setSelected(new Date());
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="text-lg font-semibold">{monthName}</div>
      <div className="flex gap-2">
        <button onClick={prevMonth} className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700">
          ←
        </button>
        <button onClick={today} className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700">
          Today
        </button>
        <button onClick={nextMonth} className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700">
          →
        </button>
      </div>
    </div>
  );
}

// ============================
// Calendar Grid (date only, linear color from red->green per %)
// ============================

function CalendarGrid({ data, setSelected, selected }) {
  const monthCursor = selected;

  const gridDays = useMemo(() => {
    const start = startOfMonth(monthCursor);
    const end = endOfMonth(monthCursor);
    const startWeekday = (start.getDay() + 6) % 7; // Monday-first
    const days = [];
    for (let i = 0; i < startWeekday; i++) {
      const d = addDays(start, i - startWeekday);
      days.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= end.getDate(); d++) {
      const dt = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d);
      days.push({ date: dt, inMonth: true });
    }
    while (days.length < 42) {
      const last = days[days.length - 1].date;
      days.push({ date: addDays(last, 1), inMonth: false });
    }
    return days;
  }, [monthCursor]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 bg-slate-900/60 border border-slate-800/60 shadow-xl"
    >
      <MonthSwitcher selected={selected} setSelected={setSelected} />
      <div className="grid grid-cols-7 text-center text-slate-400 text-xs mb-2">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {gridDays.map(({ date, inMonth }, idx) => {
          const key = fmtKey(date);
          const dayData = data[key];
          const p = calcProductivity(dayData); // 0..100
          const isToday = sameDay(date, new Date());

          // linear hue from red(0) to green(120) by percentage
          const hue = Math.round((p || 0) * 1.2); // 0..120
          const bgColor = inMonth ? `hsl(${hue}, 70%, 35%)` : "rgba(100,116,139,0.25)"; // slate for out-of-month

          return (
            <button
              key={idx}
              onClick={() => setSelected(date)}
              className={[
                "aspect-square rounded-lg flex items-center justify-center transition text-sm md:text-base text-white",
                "hover:scale-[1.02]",
                isToday && "ring-2 ring-cyan-400",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ backgroundColor: bgColor }}
              aria-label={`Day ${date.getDate()}`}
              title={inMonth ? `Productivity ${p}%` : "From adjacent month"}
            >
              {/* date only */}
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}

// ============================
// Target Panel
// ============================

function TargetPanel({ selectedDay, mutateDay, selectedKey }) {
  const [isOpen, setIsOpen] = useState(false);
  const [newTarget, setNewTarget] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const addTarget = (text) => {
    if (!text.trim()) return;
    mutateDay(selectedKey, (day) => {
      day.targets.push({ id: makeId(), text: text.trim(), done: false });
      return day;
    });
  };

  const toggleTarget = (id) => {
    mutateDay(selectedKey, (day) => {
      day.targets = day.targets.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
      return day;
    });
  };

  const removeTarget = (id) => {
    mutateDay(selectedKey, (day) => {
      day.targets = day.targets.filter((t) => t.id !== id);
      return day;
    });
  };

  const clearDone = () => {
    mutateDay(selectedKey, (day) => {
      day.targets = day.targets.filter((t) => !t.done);
      return day;
    });
  };

  const addBulk = () => {
    const lines = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!lines.length) return;
    mutateDay(selectedKey, (day) => {
      const extra = lines.map((text) => ({ id: makeId(), text, done: false }));
      day.targets = [...day.targets, ...extra];
      return day;
    });
    setBulkText("");
    setBulkOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 bg-slate-900/60 border border-slate-800/60 shadow-lg"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">🎯 Targets</h3>
        <div className="flex gap-2">
          <button onClick={() => setBulkOpen(true)} className="px-2 py-1 bg-purple-600 rounded text-sm">
            Bulk
          </button>
          <button onClick={() => setIsOpen(true)} className="px-2 py-1 bg-cyan-600 rounded">
            + Add
          </button>
          <button onClick={clearDone} className="px-2 py-1 bg-slate-700 rounded text-xs">
            Clear Done
          </button>
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {selectedDay.targets.length === 0 && (
          <li className="text-slate-400 text-sm">No targets yet.</li>
        )}
        {selectedDay.targets.map((t) => (
          <li key={t.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/60">
            <input type="checkbox" checked={!!t.done} onChange={() => toggleTarget(t.id)} aria-label="toggle target" />
            <span className={t.done ? "line-through opacity-60 flex-1" : "flex-1"}>{t.text}</span>
            <button
              onClick={() => removeTarget(t.id)}
              className="text-xs opacity-70 hover:opacity-100"
              aria-label="remove target"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>

      <AnimatePresence>
        {isOpen && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative bg-slate-900 p-6 rounded-2xl border border-slate-700 shadow-2xl w-full max-w-md"
            >
              <h3 className="mb-4 font-semibold text-lg">Add Target</h3>
              <input
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addTarget(newTarget);
                    setNewTarget("");
                    setIsOpen(false);
                  }
                }}
                placeholder="e.g., Solve 40 PYQs"
                className="w-full px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-cyan-500"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setIsOpen(false)} className="px-4 py-2 bg-slate-700 rounded">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    addTarget(newTarget);
                    setNewTarget("");
                    setIsOpen(false);
                  }}
                  className="px-4 py-2 bg-green-500 text-slate-900 font-bold rounded"
                >
                  Add
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bulkOpen && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setBulkOpen(false)} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-slate-900 p-6 rounded-2xl border border-slate-700 shadow-2xl w-full max-w-lg"
            >
              <h3 className="mb-2 font-semibold text-lg">Bulk Add Targets</h3>
              <p className="text-xs text-slate-400 mb-3">One line per target</p>
              <textarea
                rows={8}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                className="w-full px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-purple-500"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setBulkOpen(false)} className="px-4 py-2 bg-slate-700 rounded">
                  Cancel
                </button>
                <button onClick={addBulk} className="px-4 py-2 bg-purple-500 rounded text-black">
                  Add Lines
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================
// Quick Stats
// ============================

function QuickStats({ selectedDay, mutateDay, selectedKey }) {
  const updateStats = (patch) => {
    mutateDay(selectedKey, (day) => {
      const next = { ...day.stats, ...patch };
      next.hours = Number.isFinite(next.hours) ? next.hours : 0;
      next.tests = Number.isFinite(next.tests) ? next.tests : 0;
      next.questions = Number.isFinite(next.questions) ? next.questions : 0;
      day.stats = next;
      return day;
    });
  };

  const hours = selectedDay.stats.hours || 0;
  const tests = selectedDay.stats.tests || 0;
  const questions = selectedDay.stats.questions || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 bg-slate-900/60 border border-slate-800/60 shadow-lg"
    >
      <h3 className="text-lg font-semibold mb-3">📈 Quick Stats</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <label className="flex flex-col">
          <span className="text-slate-400">Hours Studied</span>
          <input
            type="number"
            step="0.25"
            value={hours}
            onChange={(e) => updateStats({ hours: Number(e.target.value) })}
            className="px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-emerald-500"
          />
        </label>
        <label className="flex flex-col">
          <span className="text-slate-400">Tests Attempted</span>
          <input
            type="number"
            value={tests}
            onChange={(e) => updateStats({ tests: Number(e.target.value) })}
            className="px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-pink-500"
          />
        </label>
        <label className="flex flex-col col-span-2">
          <span className="text-slate-400">Questions Solved</span>
          <input
            type="number"
            value={questions}
            onChange={(e) => updateStats({ questions: Number(e.target.value) })}
            className="px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-sky-500"
          />
        </label>
      </div>
    </motion.div>
  );
}

// ============================
// NEW: This Week vs Last Week Analysis
// ============================

function WeekAnalysis({ data }) {
  const today = new Date();

  // current week (Mon..Sun) covering today
  const thisWeekStart = startOfWeek(today);
  const thisWeekEnd = endOfWeek(today);

  // last week block
  theLoop: {
    // nothing; just ensuring scoping if needed in future
  }
  const lastWeekEnd = addDays(thisWeekStart, -1);
  const lastWeekStart = startOfWeek(lastWeekEnd);

  const thisKeys = keysBetween(thisWeekStart, thisWeekEnd);
  const lastKeys = keysBetween(lastWeekStart, lastWeekEnd);

  const thisAgg = sumStatsForKeys(data, thisKeys);
  const lastAgg = sumStatsForKeys(data, lastKeys);

  const delta = (a, b) => {
    const d = a - b;
    const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
    return { text: `${sign}${Math.abs(d).toFixed(2)}`, up: d > 0, down: d < 0 };
  };

  const hoursDelta = delta(thisAgg.hours, lastAgg.hours);
  const testsDelta = delta(thisAgg.tests, lastAgg.tests);
  const qsDelta = delta(thisAgg.questions, lastAgg.questions);
  const prodDelta = delta(thisAgg.avgProd, lastAgg.avgProd);

  const Tile = ({ label, value, delta, unit = "" }) => (
    <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-2xl font-semibold">{value}{unit}</div>
      <div
        className={[
          "mt-1 text-xs",
          delta.up && "text-emerald-400",
          delta.down && "text-rose-400",
          !delta.up && !delta.down && "text-slate-400",
        ].join(" ")}
      >
        {delta.text} vs last wk
      </div>
    </div>
  );

  const MiniBar = ({ thisVal, lastVal, label }) => {
    // tiny bar compare
    const max = Math.max(thisVal, lastVal, 1);
    const thisPct = Math.round((thisVal / max) * 100);
    const lastPct = Math.round((lastVal / max) * 100);
    return (
      <div className="space-y-1">
        <div className="text-xs text-slate-400">{label}</div>
        <div className="h-2 rounded bg-slate-800 overflow-hidden">
          <div className="h-2 bg-cyan-500" style={{ width: `${thisPct}%` }} />
        </div>
        <div className="h-2 rounded bg-slate-800 overflow-hidden">
          <div className="h-2 bg-purple-500" style={{ width: `${lastPct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-slate-400">
          <span>This: {thisVal.toFixed?.(2) ?? thisVal}</span>
          <span>Last: {lastVal.toFixed?.(2) ?? lastVal}</span>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800/60 shadow-xl"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">📅 This Week vs Last Week</h3>
        <div className="text-xs text-slate-400">
          {thisWeekStart.toLocaleDateString()} – {thisWeekEnd.toLocaleDateString()}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Hours" value={thisAgg.hours.toFixed(2)} delta={hoursDelta} />
        <Tile label="Tests" value={thisAgg.tests.toFixed(0)} delta={testsDelta} />
        <Tile label="Questions" value={thisAgg.questions.toFixed(0)} delta={qsDelta} />
        <Tile label="Avg Productivity" value={thisAgg.avgProd} delta={prodDelta} unit="%" />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniBar label="Hours" thisVal={thisAgg.hours} lastVal={lastAgg.hours} />
        <MiniBar label="Tests" thisVal={thisAgg.tests} lastVal={lastAgg.tests} />
        <MiniBar label="Questions" thisVal={thisAgg.questions} lastVal={lastAgg.questions} />
      </div>

      {(thisAgg.targetsTotal + lastAgg.targetsTotal) > 0 && (
        <div className="mt-4 text-xs text-slate-300">
          Targets done this wk:{" "}
          <span className="font-semibold">
            {thisAgg.targetsDone}/{thisAgg.targetsTotal}
          </span>{" "}
          ({thisAgg.targetsTotal ? Math.round((thisAgg.targetsDone / thisAgg.targetsTotal) * 100) : 0}%)
          <span className="opacity-60 mx-2">•</span>
          last wk:{" "}
          <span className="font-semibold">
            {lastAgg.targetsDone}/{lastAgg.targetsTotal}
          </span>{" "}
          ({lastAgg.targetsTotal ? Math.round((lastAgg.targetsDone / lastAgg.targetsTotal) * 100) : 0}%)
        </div>
      )}
    </motion.div>
  );
}

// ============================
// NEW: Averages Panel (7d / 30d / All-time) — right column
// ============================

function AveragesPanel({ data }) {
  const today = new Date();

  const sevenKeys = lastNDays(7, today).map(fmtKey);
  const thirtyKeys = lastNDays(30, today).map(fmtKey);
  const allKeys = Object.keys(data).sort();

  const sAgg = sumStatsForKeys(data, sevenKeys);
  const tAgg = sumStatsForKeys(data, thirtyKeys);
  const aAgg = sumStatsForKeys(data, allKeys);

  // averages per day over window size (even if blank days exist → true rolling averages)
  const sDays = 7, tDays = 30, aDays = allKeys.length || 1;

  const rows = [
    {
      label: "7 days",
      hrs: averageOf(sAgg.hours, sDays),
      qs: averageOf(sAgg.questions, sDays),
      ts: averageOf(sAgg.tests, sDays),
    },
    {
      label: "30 days",
      hrs: averageOf(tAgg.hours, tDays),
      qs: averageOf(tAgg.questions, tDays),
      ts: averageOf(tAgg.tests, tDays),
    },
    {
      label: "All-time",
      hrs: averageOf(aAgg.hours, aDays),
      qs: averageOf(aAgg.questions, aDays),
      ts: averageOf(aAgg.tests, aDays),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800/60 shadow-xl"
    >
      <h3 className="text-lg font-semibold mb-3">📊 Averages (per day)</h3>
      <div className="overflow-hidden rounded-xl border border-slate-800/60">
        <div className="grid grid-cols-4 text-xs bg-slate-800/60 px-3 py-2">
          <div className="font-semibold">Window</div>
          <div className="font-semibold">Avg Hours</div>
          <div className="font-semibold">Avg Questions</div>
          <div className="font-semibold">Avg Tests</div>
        </div>
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-4 px-3 py-2 border-t border-slate-800/60 text-sm">
            <div className="text-slate-300">{r.label}</div>
            <div className="font-mono">{r.hrs}</div>
            <div className="font-mono">{r.qs}</div>
            <div className="font-mono">{r.ts}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-slate-400">
        Averages include zero-activity days in the window (realistic rolling averages).
      </div>
    </motion.div>
  );
}

// ============================
// Progress Graph (questions scaled to ÷10; averages separate)
// ============================

function ProgressGraph({ data }) {
  const [range, setRange] = useState("7d");
  const [chartType, setChartType] = useState("line");

  const chartData = useMemo(() => {
    const today = new Date();
    const allKeys = Object.keys(data);
    if (!allKeys.length) return [];
    let from;

    if (range === "7d")
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
    else if (range === "30d")
      from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
    else from = new Date(Math.min(...allKeys.map((k) => +new Date(k))));

    return rangeDays(from, today).map((d) => {
      const k = fmtKey(d);
      const day = data[k];
      const hours = day?.stats?.hours || 0;
      const tests = day?.stats?.tests || 0;
      const questions = day?.stats?.questions || 0;
      const q10 = Math.round(questions / 10); // << scale to 1/10
      return {
        date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        hours,
        tests,
        q10,
      };
    });
  }, [data, range]);

  const chartEl = useMemo(() => {
    if (!chartData.length) return <div />;

    const common = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
        <XAxis dataKey="date" tick={{ fill: "#cbd5e1" }} />
        <YAxis tick={{ fill: "#cbd5e1" }} />
        <Tooltip />
        <Legend />
      </>
    );

    switch (chartType) {
      case "bar":
        return (
          <BarChart data={chartData}>
            {common}
            <Bar dataKey="hours" fill="#22c55e" name="Study Hours" />
            <Bar dataKey="tests" fill="#f472b6" name="Tests" />
            <Bar dataKey="q10" fill="#38bdf8" name="Questions (÷10)" />
          </BarChart>
        );
      case "area":
        return (
          <AreaChart data={chartData}>
            {common}
            <Area dataKey="hours" fill="#22c55e55" stroke="#22c55e" name="Study Hours" />
            <Area dataKey="tests" fill="#f472b655" stroke="#f472b6" name="Tests" />
            <Area dataKey="q10" fill="#38bdf855" stroke="#38bdf8" name="Questions (÷10)" />
          </AreaChart>
        );
      default:
        return (
          <LineChart data={chartData}>
            {common}
            <Line type="monotone" dataKey="hours" stroke="#22c55e" name="Study Hours" />
            <Line type="monotone" dataKey="tests" stroke="#f472b6" name="Tests" />
            <Line type="monotone" dataKey="q10" stroke="#38bdf8" name="Questions (÷10)" />
          </LineChart>
        );
    }
  }, [chartType, chartData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 bg-slate-900/60 border border-slate-800/60 shadow-xl"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">📊 Progress Graph</h3>
        <div className="flex gap-2">
          {["7d", "30d", "all"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2 py-1 rounded ${range === r ? "bg-cyan-600" : "bg-slate-800"}`}
            >
              {r}
            </button>
          ))}
          {["line", "bar", "area"].map((t) => (
            <button
              key={t}
              onClick={() => setChartType(t)}
              className={`px-2 py-1 rounded ${chartType === t ? "bg-purple-600" : "bg-slate-800"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartEl}
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}

// ============================
// Mistakes Manager
// ============================

function MistakesManager({ selectedDay, mutateDay, selectedKey }) {
  const [newMistake, setNewMistake] = useState("");
  const [tag, setTag] = useState("Concept");
  const [filter, setFilter] = useState("All");

  const addMistake = () => {
    if (!newMistake.trim()) return;
    mutateDay(selectedKey, (day) => {
      day.stats.mistakes = [...(day.stats.mistakes || []), { id: makeId(), text: newMistake.trim(), tag }];
      return day;
    });
    setNewMistake("");
  };

  const removeMistake = (id) => {
    mutateDay(selectedKey, (day) => {
      day.stats.mistakes = (day.stats.mistakes || []).filter((m) => m.id !== id);
      return day;
    });
  };

  const mistakes = (selectedDay.stats.mistakes || []).filter((m) => filter === "All" || m.tag === filter);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-2xl bg-slate-900/60 border border-slate-700/40 shadow-lg"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold mb-3">❌ Smart Mistakes Manager</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 rounded bg-slate-800" title="Filter">
          <option>All</option>
          <option>Formula</option>
          <option>Concept</option>
          <option>Silly error</option>
        </select>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          value={newMistake}
          onChange={(e) => setNewMistake(e.target.value)}
          placeholder="Write mistake..."
          className="flex-1 px-3 py-2 rounded bg-slate-800 outline-none focus:ring-2 ring-rose-500"
        />
        <select value={tag} onChange={(e) => setTag(e.target.value)} className="px-2 py-2 rounded bg-slate-800">
          <option>Formula</option>
          <option>Concept</option>
          <option>Silly error</option>
        </select>
        <button onClick={addMistake} className="px-4 py-2 bg-red-500 text-white rounded">
          Add
        </button>
      </div>
      <ul className="space-y-2 max-h-56 overflow-auto pr-1">
        {mistakes.map((m) => (
          <li key={m.id} className="flex justify-between items-center p-2 bg-slate-800/60 rounded">
            <span className="mr-2">
              <span className="opacity-70">[{m.tag}]</span> {m.text}
            </span>
            <button onClick={() => removeMistake(m.id)} className="text-xs text-red-300 hover:text-red-500">
              ✕
            </button>
          </li>
        ))}
      </ul>
      {selectedDay.stats.mistakes?.length > 0 && (
        <div className="mt-4 text-sm text-slate-300">
          📘 Revision Sheet: {selectedDay.stats.mistakes.map((m) => m.text).join(", ")}
        </div>
      )}
    </motion.div>
  );
}

// ============================
// Focus Timer (floating)
// ============================

function FocusTimer({ mutateDay, selectedKey }) {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("Focus");
  const [focusLen, setFocusLen] = useState(25 * 60);
  const [breakLen, setBreakLen] = useState(5 * 60);

  const target = mode === "Focus" ? focusLen : breakLen;
  const remaining = Math.max(0, target - seconds);

  useEffect(() => {
    let timer;
    if (running) timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (!running) return;
    if (seconds >= target) {
      setRunning(false);
      setMode((m) => (m === "Focus" ? "Break" : "Focus"));
      setSeconds(0);
    }
  }, [seconds, target, running]);

  const toggleTimer = () => setRunning((r) => !r);
  const resetTimer = () => setSeconds(0);

  const saveHours = () => {
    const hours = seconds / 3600;
    mutateDay(selectedKey, (day) => {
      day.stats.hours = (day.stats.hours || 0) + Number(hours.toFixed(2));
      return day;
    });
    resetTimer();
    setRunning(false);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <motion.div
      drag
      dragMomentum={false}
      className="fixed bottom-6 right-6 p-4 rounded-2xl bg-slate-900/90 border border-slate-700/40 shadow-2xl w-64 cursor-move"
    >
      <h3 className="text-sm font-semibold mb-2">⏳ Focus Timer</h3>
      <div className="text-xs text-slate-400 mb-1">Mode: {mode}</div>
      <div className="text-2xl font-mono mb-3">{formatTime(remaining)}</div>
      <div className="flex gap-2 mb-2">
        <button onClick={toggleTimer} className="flex-1 px-3 py-2 bg-cyan-600 rounded">
          {running ? "Pause" : "Start"}
        </button>
        <button onClick={saveHours} className="flex-1 px-3 py-2 bg-green-500 text-black rounded">
          Save
        </button>
      </div>
      <div className="flex gap-2 text-xs">
        <button onClick={() => setMode("Focus")} className={`px-2 py-1 rounded ${mode === "Focus" ? "bg-slate-700" : "bg-slate-800"}`}>
          Focus
        </button>
        <button onClick={() => setMode("Break")} className={`px-2 py-1 rounded ${mode === "Break" ? "bg-slate-700" : "bg-slate-800"}`}>
          Break
        </button>
        <button onClick={resetTimer} className="px-2 py-1 rounded bg-slate-800">
          Reset
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <label className="flex items-center gap-1">
          <span className="w-12">Foc</span>
          <input
            type="number"
            min={5}
            max={180}
            value={Math.round(focusLen / 60)}
            onChange={(e) => setFocusLen(clamp(Number(e.target.value) * 60, 300, 10800))}
            className="w-16 px-2 py-1 rounded bg-slate-800"
          />
          <span>min</span>
        </label>
        <label className="flex items-center gap-1">
          <span className="w-12">Break</span>
          <input
            type="number"
            min={1}
            max={60}
            value={Math.round(breakLen / 60)}
            onChange={(e) => setBreakLen(clamp(Number(e.target.value) * 60, 60, 3600))}
            className="w-16 px-2 py-1 rounded bg-slate-800"
          />
          <span>min</span>
        </label>
      </div>
    </motion.div>
  );
}

// ============================
// Data Tools
// ============================

function DataTools({ data, setData, targetDateStr, setTargetDateStr, mission, setMission, addToast }) {
  const fileInputRef = useRef(null);

  const handleExport = () => {
    const bundle = { app: "jee-dashboard", version: 3, exportedAt: new Date().toISOString(), payload: { days: data, countdownTarget: targetDateStr, mission } };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "jee-dashboard-backup.json"; a.click();
    URL.revokeObjectURL(url);
    addToast("Exported JSON backup.", "success");
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImport = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = typeof ev.target.result === "string" ? ev.target.result : "";
        const imported = JSON.parse(text || "{}");
        if (imported.payload?.days) setData(imported.payload.days);
        if (imported.payload?.countdownTarget) setTargetDateStr(imported.payload.countdownTarget);
        if (imported.payload?.mission) setMission(imported.payload.mission);
        addToast("Imported data successfully.", "success");
      } catch {
        addToast("Invalid file format.", "error");
      }
    };
    reader.readAsText(file);
  };

  const handleCSVExport = () => {
    const keys = Object.keys(data).sort();
    const rows = [["date", "hours", "tests", "questions", "productivity", "targets", "done"]];
    for (const k of keys) {
      const day = data[k] || defaultDay();
      const p = calcProductivity(day);
      const totalT = day.targets?.length || 0;
      const doneT = day.targets?.filter((t) => t.done).length || 0;
      rows.push([k, day.stats?.hours || 0, day.stats?.tests || 0, day.stats?.questions || 0, p, totalT, doneT]);
    }
    const blob = new Blob([toCSV(rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "jee-dashboard-stats.csv"; a.click();
    URL.revokeObjectURL(url);
    addToast("Exported CSV.", "success");
  };

  const loadDemoData = () => {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 59);
    const next = {};
    rangeDays(from, today).forEach((d) => {
      const hrs = Math.max(0, Math.round((Math.sin(d.getDate() / 2) + 1) * 3 + Math.random() * 2));
      const tests = Math.round(Math.random() * 2);
      const qs = Math.round(hrs * (20 + Math.random() * 15));
      const tcount = Math.round(2 + Math.random() * 4);
      const targets = Array.from({ length: tcount }).map((_, i) => ({ id: makeId(), text: `Task ${i + 1}`, done: Math.random() > 0.4 }));
      next[fmtKey(d)] = { targets, stats: { hours: hrs, tests, questions: qs, mistakes: [] } };
    });
    setData((old) => ({ ...old, ...next }));
    addToast("Demo data (last 60 days) loaded.", "success");
  };

  const clearAll = () => { setData({}); addToast("Cleared all stored days.", "info"); };

  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={handleExport} className="px-4 py-2 bg-cyan-600 rounded">Export JSON</button>
      <button onClick={handleImportClick} className="px-4 py-2 bg-green-600 rounded">Import JSON</button>
      <input ref={fileInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" />
      <button onClick={handleCSVExport} className="px-4 py-2 bg-indigo-600 rounded">Export CSV</button>
      <button onClick={loadDemoData} className="px-4 py-2 bg-purple-600 rounded">Load Demo</button>
      <button onClick={clearAll} className="px-4 py-2 bg-slate-700 rounded">Clear All</button>
    </div>
  );
}

// ============================
// Settings Panel (with hue slider)
// ============================

function SettingsPanel({ data, setData, targetDateStr, mission, setTargetDateStr, setMission, addToast, bgHue, setBgHue }) {
  const [open, setOpen] = useState(false);

  const previewStyle = useMemo(() => {
    const a = `hsl(${bgHue}, 45%, 12%)`;
    const b = `hsl(${(bgHue + 25) % 360}, 50%, 16%)`;
    const c = `hsl(${(bgHue + 55) % 360}, 55%, 20%)`;
    return { backgroundImage: `linear-gradient(135deg, ${a}, ${b}, ${c})` };
  }, [bgHue]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-slate-900/60 border border-slate-700/40 shadow-lg">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">⚙ Settings & Data</h3>
        <button onClick={() => setOpen((v) => !v)} className="px-2 py-1 bg-slate-800 rounded">{open ? "Hide" : "Open"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex flex-col">
              <span className="text-slate-400 text-xs">Mission</span>
              <input value={mission} onChange={(e) => setMission(e.target.value)} className="px-3 py-2 rounded bg-slate-800" />
            </label>
            <label className="flex flex-col">
              <span className="text-slate-400 text-xs">Countdown Target</span>
              <input type="date" value={targetDateStr} onChange={(e) => setTargetDateStr(e.target.value)} className="px-3 py-2 rounded bg-slate-800" />
            </label>
            <div className="flex flex-col">
              <span className="text-slate-400 text-xs mb-1">Background Hue ({bgHue}°)</span>
              <input type="range" min={0} max={359} step={1} value={bgHue} onChange={(e) => setBgHue(Number(e.target.value))} className="w-full" />
              <div className="mt-2 h-10 w-full rounded-lg border border-slate-700" style={previewStyle} />
            </div>
          </div>

          <div>
            <div className="mb-2 font-semibold">Backup / Restore</div>
            <DataTools
              data={data}
              setData={setData}
              targetDateStr={targetDateStr}
              setTargetDateStr={setTargetDateStr}
              mission={mission}
              setMission={setMission}
              addToast={addToast}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ============================
// Footer
// ============================

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
const Footer = () => {
  const now = useNow(1000);
  return (
    <div className="py-6 text-xs text-slate-400 flex items-center justify-between flex-wrap gap-2">
      <div>Local time: <span className="text-slate-300 font-mono">{now.toLocaleString()}</span></div>
      <div className="flex items-center gap-3"><span>Made for JEE 2026 momentum 🚀</span></div>
    </div>
  );
};

// ============================
// MAIN APP
// ============================

export default function App() {
  const [data, setData] = useLocalStore("jee-dashboard-v3", {});
  const [selected, setSelected] = useState(new Date());
  const [targetDateStr, setTargetDateStr] = useLocalStore("jee-target", "2026-01-22");
  const [mission, setMission] = useLocalStore("jee-mission", "99.5%ILE +");
  const [bgHue, setBgHue] = useLocalStore("jee-bg-hue", 220); // dark bluish default

  const { toasts, addToast } = useToasts();

  const selectedKey = fmtKey(selected);
  const selectedDay = data[selectedKey] || defaultDay();

  function mutateDay(dateKey, updater) {
    setData((old) => {
      const prev = old[dateKey] || defaultDay();
      const next = updater({
        ...prev,
        targets: [...(prev.targets || [])],
        stats: { ...(prev.stats || { hours: 0, tests: 0, questions: 0, mistakes: [] }) },
      });
      return { ...old, [dateKey]: next };
    });
  }

  const appBgStyle = useMemo(() => {
    const a = `hsl(${bgHue}, 45%, 12%)`;
    const b = `hsl(${(bgHue + 25) % 360}, 50%, 16%)`;
    const c = `hsl(${(bgHue + 55) % 360}, 55%, 20%)`;
    return { backgroundImage: `linear-gradient(135deg, ${a}, ${b}, ${c})` };
  }, [bgHue]);

  return (
    <div className="min-h-screen font-sans text-white" style={appBgStyle}>
      <ToastHost toasts={toasts} />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <CountdownHeader targetDateStr={targetDateStr} setTargetDateStr={setTargetDateStr} mission={mission} setMission={setMission} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <CalendarGrid data={data} setSelected={setSelected} selected={selected} />
          <div className="space-y-6">
            <TargetPanel selectedDay={selectedDay} mutateDay={mutateDay} selectedKey={selectedKey} />
            <QuickStats selectedDay={selectedDay} mutateDay={mutateDay} selectedKey={selectedKey} />
          </div>
          <div className="space-y-6">
            {/* NEW right column content */}
            <AveragesPanel data={data} />
          </div>
        </div>

        {/* NEW week-vs-week analysis */}
        <WeekAnalysis data={data} />

        <ProgressGraph data={data} />

        <MistakesManager selectedDay={selectedDay} mutateDay={mutateDay} selectedKey={selectedKey} />

        <SettingsPanel
          data={data}
          setData={setData}
          targetDateStr={targetDateStr}
          mission={mission}
          setTargetDateStr={setTargetDateStr}
          setMission={setMission}
          addToast={addToast}
          bgHue={bgHue}
          setBgHue={setBgHue}
        />

        <Footer />
      </div>

      <FocusTimer mutateDay={mutateDay} selectedKey={selectedKey} />
    </div>
  );
}
